import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import moment from 'moment'
import { Box, Card, Grid, Tooltip, Typography } from '@mui/material'
import { Clear } from '@mui/icons-material'
import { filter, find, flatMap, groupBy, head, includes, keys, orderBy, toLower } from 'lodash'
import { shallowEqual, useSelector } from 'react-redux'
import { ShapeIAEauModel } from '../../../../iaeau/shapes/IAEauShapes'
import { getSiteUrl } from '../../../../utils/mapUtils/SiteTypes'
import { STATION_NAME_ASSOCIATION, STATION_TYPE_NAME } from '../../../constants/StationConstants'
import { getYearDate } from '../../../../utils/DateUtil'
import useProgressDispatch from 'utils/customHook/useProgressDispatch'
import PiezometerStationAction from 'station/actions/PiezometerStationAction'
import HydrometryAction from 'hydrometry/actions/HydrometryAction'
import PluviometryAction from 'pluviometry/actions/PluviometryAction'
import ProgressCard from 'components/card/ProgressCard'
import { CardTable, NewTable } from 'components/datatable/NewTable'
import { nbPerPageLabelTiny } from 'referencial/constants/ReferencialConstants'
import { SmallStyledFieldSet, StyledLegend } from 'components/StyledElements'
import Input, { NewInput } from 'components/forms/Input'
import ListComponent from 'components/list/tableList/ListComponent'
import { filterAllCharacters } from 'utils/StringUtil'
import IntervalDateChart from 'components/echart/IntervalDateChart'
import DataInputs from '../DataInputs'
import { isGenericIAModel } from 'iaeau/utils/IAEauUtils'
import IAEauAction from 'iaeau/IAEauAction'

const PREDEFINED_MODEL = -1
const HEADERS_SEARCH_VALUE_STATION = ['id', 'code', 'name']
const HEADERS_ADD_STATION_TABLE = ['code', 'name']
const HEADERS_SELECTED_STATIONS_TABLE = ['nullValue', 'type', 'code', 'name']
const ECMWF_SOURCE = 'ecmwf'
const EXPERT_CLICK_THRESHOLD = 5
const CHART_HEIGHT = 300
const VERSION_DEBOUNCE_DELAY = 1000

const HEADER_TEXT_SX = { textAlign: 'center', marginTop: 1 }

const getStationsByType = (stations, typeName) => orderBy(filter(stations, { typeName }), 'id')

const AddStationComponent = ({
    stations = [],
    tableStations = [],
    changeParams = () => {},
}) => {
    const {
        piezometers,
        pluviometers,
        hydrometricStations,
    } = useSelector(store => ({
        piezometers: store.PiezometryReducer.piezometersLight,
        pluviometers: store.PluviometryReducer.pluviometers,
        hydrometricStations: store.HydrometryReducer.hydrometricStations,
    }), shallowEqual)

    const [searchValue, setSearchValue] = useState('')

    const allStations = filterAllCharacters([...piezometers, ...hydrometricStations, ...pluviometers], HEADERS_SEARCH_VALUE_STATION, searchValue)
        .filter(station => !find(tableStations, associated => associated.stationLinkedId === station.id && associated.typeName === station.typeName))

    const groupedStations = groupBy(allStations, 'typeName')
    const groupTables = keys(groupedStations).map(typeName => {
        const count = groupedStations[typeName].length
        return {
            title: (
                <Grid container columnSpacing={2} padding={1} sx={{ alignItems: 'center' }}>
                    <Grid>
                        <Box component='img' src={getSiteUrl(STATION_NAME_ASSOCIATION[typeName])} sx={{ maxHeight: 30 }} alt='' />
                    </Grid>
                    <Grid>
                        <Typography variant='h6'>{i18n[typeName]}</Typography>
                    </Grid>
                    <Grid container size='grow' sx={{ justifyContent: 'flex-end' }}>
                        <Grid>
                            <Typography variant='h6'>{`${count} ${i18n.stations}`}</Typography>
                        </Grid>
                    </Grid>
                </Grid>
            ),
            component: (
                <NewTable
                    rows={groupedStations[typeName]}
                    rowsPerPageOptions={nbPerPageLabelTiny}
                    onClickRow={station => changeParams({ stations: [...stations, station] })}
                    headers={HEADERS_ADD_STATION_TABLE}
                />
            ),
        }
    })

    return (
        <Card title={i18n.addStation}>
            <Grid container rowSpacing={1}>
                <Grid size={6} padding={1}>
                    <Input
                        tooltip={i18n.searchThenEnter}
                        title={i18n.search}
                        value={searchValue}
                        onEnterKeyPress={setSearchValue}
                    />
                </Grid>
                <Grid size={12}>
                    <ListComponent tables={groupTables} accordion />
                </Grid>
            </Grid>
        </Card>
    )
}

AddStationComponent.propTypes = {
    stations: PropTypes.arrayOf(PropTypes.shape({})),
    tableStations: PropTypes.arrayOf(PropTypes.shape({})),
    changeParams: PropTypes.func,
}

const StationInputs = ({
    stations = [],
    stationType,
    id,
    selectedModel = {},
    changeParams = () => {},
}) => {
    const stationsFormatted = stations.map(station => ({
        ...station,
        nullValue: (station.typeName === stationType && `${station.id}` === id) || selectedModel.idScenario === PREDEFINED_MODEL ? null : (
            <Clear
                sx={{ cursor: 'pointer' }}
                onClick={() => changeParams({ stations: stations.filter(link => link.typeName !== station.typeName || link.id !== station.id) })}
            />
        ),
        type: (
            <Tooltip title={i18n[station.typeName]} arrow>
                <Box
                    component='img'
                    src={getSiteUrl(STATION_NAME_ASSOCIATION[station.typeName])}
                    alt=''
                    sx={{ maxHeight: 30, padding: '2px 0', display: 'inline-block' }}
                />
            </Tooltip>
        ),
    }))

    return (
        <SmallStyledFieldSet>
            <StyledLegend>{i18n.stations}</StyledLegend>
            <Grid container rowSpacing={2}>
                <Grid size={12}>
                    <CardTable
                        title={i18n.selectedStations}
                        rows={stationsFormatted}
                        rowsPerPageOptions={nbPerPageLabelTiny}
                        headers={HEADERS_SELECTED_STATIONS_TABLE}
                    />
                </Grid>
            </Grid>
        </SmallStyledFieldSet>
    )
}

StationInputs.propTypes = {
    selectedModel: ShapeIAEauModel,
    changeParams: PropTypes.func,
    stations: PropTypes.arrayOf(PropTypes.shape({})),
    stationType: PropTypes.string,
    id: PropTypes.string,
}

const ModelStepInputs = ({
    selectedModel = {},
    changeParams = () => {},
    changeModel = () => {},
    stations = [],
    stationType,
    id,
    onAvailabilityChange = () => {},
}) => {
    const [stats, setStats] = useState([])
    const [predStats, setPredStats] = useState([])
    const [clickCount, setClickCount] = useState(0)

    const piezos = useMemo(() => getStationsByType(stations, STATION_TYPE_NAME.piezometry), [stations])
    const hydros = useMemo(() => getStationsByType(stations, STATION_TYPE_NAME.hydrometry), [stations])
    const pluvios = useMemo(() => getStationsByType(stations, STATION_TYPE_NAME.pluviometry), [stations])

    const orderedStations = useMemo(() => [...piezos, ...hydros, ...pluvios], [piezos, hydros, pluvios])
    const stationsKey = useMemo(() => (stations || []).map(station => `${station.id}-${station.typeName}`).join(','), [stations])

    const { isLoaded } = useProgressDispatch(() => {
        const firstPluvioId = head(pluvios)?.id
        const promises = [
            ...piezos.map(station => PiezometerStationAction.promisePiezoMeasuresStats(station.id).catch(() => [])),
            ...hydros.map(station => HydrometryAction.promiseHydroStats(station.id).catch(() => [])),
            ...pluvios.map(station => PluviometryAction.promisePluviometerMeasuresStats(station.id).catch(() => [])),
        ]
        const shouldFetchPredStats = isGenericIAModel(selectedModel)
        const predPromise = (firstPluvioId && shouldFetchPredStats)
            ? IAEauAction.promisePredStats(STATION_TYPE_NAME.pluviometry, firstPluvioId).then(result => setPredStats(result || [])).catch(() => setPredStats([]))
            : Promise.resolve(setPredStats([]))

        return [
            Promise.all(promises).then(setStats).catch(() => setStats([])),
            predPromise,
        ]
    }, [stationsKey])

    const stationsFormatted = useMemo(() => {
        const base = flatMap(orderedStations, (station, idx) => station.dataTypes?.map(dataType => {
            const stationStat = find(stats[idx], { typeId: dataType }) || {}
            return {
                name: `${station.name} - ${stationStat.label || ''}`,
                minDate: getYearDate(stationStat.startDate),
                maxDate: getYearDate(stationStat.endDate || moment().valueOf()),
            }
        }) || [])

        if (isGenericIAModel(selectedModel) && predStats?.length) {
            const ecmwfPred = find(predStats, stat => stat.source && includes(toLower(stat.source), ECMWF_SOURCE))
            if (ecmwfPred?.minSimulationDate && ecmwfPred?.maxSimulationDate) {
                return [
                    ...base,
                    {
                        name: i18n.rainForecastEcmwf,
                        minDate: getYearDate(ecmwfPred.minSimulationDate),
                        maxDate: getYearDate(ecmwfPred.maxSimulationDate),
                    },
                ]
            }
        }

        return base
    }, [orderedStations, stats, selectedModel, predStats])

    const isStatsLoading = !isLoaded || (orderedStations.length > 0 && stats.length < orderedStations.length)

    if (isStatsLoading) {
        return <ProgressCard indeterminate />
    }

    return !stats.length ? (
        <Grid size={12} sx={HEADER_TEXT_SX}>
            <Typography variant='h6'>{i18n.noData}</Typography>
        </Grid>
    ) : (
        <Grid container size={12} rowSpacing={2}>
            <Grid size={12} sx={HEADER_TEXT_SX}>
                <Typography variant='h6'>{i18n.selectDataUsedToModel}</Typography>
            </Grid>
            <Grid size={12}>
                <StationInputs
                    stations={stations}
                    stationType={stationType}
                    id={id}
                    selectedModel={selectedModel}
                    changeParams={changeParams}
                />
            </Grid>
            <Grid size={12}>
                <DataInputs
                    selectedModel={selectedModel}
                    stations={orderedStations}
                    stats={stats}
                    predStats={predStats}
                    stationType={stationType}
                    isLoading={isStatsLoading}
                    onAvailabilityChange={onAvailabilityChange}
                />
            </Grid>
            <Grid size={12}>
                <SmallStyledFieldSet>
                    <StyledLegend onClick={() => setClickCount(current => current + 1)}>{i18n.dataAvailability}</StyledLegend>
                    <Box sx={{ overflow: 'hidden' }}>
                        <IntervalDateChart
                            data={stationsFormatted}
                            height={CHART_HEIGHT}
                        />
                    </Box>
                </SmallStyledFieldSet>
            </Grid>
            {clickCount >= EXPERT_CLICK_THRESHOLD && (
                <Grid size={12}>
                    <SmallStyledFieldSet>
                        <StyledLegend>/!\ Mode Expert /!\</StyledLegend>
                        <NewInput
                            value={selectedModel.version}
                            onChange={version => changeModel({ version })}
                            title={i18n.version}
                            debounceDelay={VERSION_DEBOUNCE_DELAY}
                        />
                    </SmallStyledFieldSet>
                </Grid>
            )}
        </Grid>
    )
}

ModelStepInputs.propTypes = {
    selectedModel: ShapeIAEauModel,
    changeParams: PropTypes.func,
    changeModel: PropTypes.func,
    stations: PropTypes.arrayOf(PropTypes.shape({})),
    stationType: PropTypes.string,
    id: PropTypes.string,
    onAvailabilityChange: PropTypes.func,
}

export default ModelStepInputs